import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, Zoom, Mousewheel} from 'swiper/modules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBuilding, faLocationDot, faCalendarDays, faMoneyBill } from '@fortawesome/free-solid-svg-icons';
import LoadingImage from '../Common/LoadingImage';
import ImageModal from '../Common/ImageModal';
import CountryDisplay from '../Common/CountryDisplay';

const ProjectDetailMobile = ({ project, language, siteInfo }) => {
  const { t } = useTranslation();
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const gallery_images = project?.gallery_images || [];
  const isRTL = language == 'ar' ? true : false;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-[50vh]">
        <LoadingImage
          src={project.hero_image}
          alt={project.title}
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70">
          <div className="container flex flex-col justify-end px-4 pb-8 mx-auto h-full">
            <div className="flex flex-wrap gap-2 items-center mb-3">
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                project.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'
              } text-white`}>
                {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
              </span>
              <span className="text-sm text-white">{project.location}</span>
            </div>
            <h1 className="mb-2 text-3xl font-bold text-white">{project.title}</h1>
          </div>
        </div>
      </section>

      <div className="container px-4 py-6 mx-auto">
        {/* Project Details */}
        <div className="p-6 mb-6 bg-white rounded-xl shadow-sm">
          <h2 className="mb-6 text-2xl font-bold">{t('projects.overview')}</h2>
          {project.description && (
            <div className="mb-8 pb-8 border-b border-gray-100">
              <p className="text-gray-600 leading-relaxed text-justify">{project.description}</p>
            </div>
          )}
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-100">
                <FontAwesomeIcon icon={faBuilding} className="text-stone-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">{t('projects.type')}</h3>
                <p className="text-lg font-semibold text-gray-900">{project.type}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-100">
                <FontAwesomeIcon icon={faLocationDot} className="text-stone-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">{t('projects.location')}</h3>
                <CountryDisplay 
                      countryCode={project.country_code}
                      city={project.city}
                    />
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-100">
                <FontAwesomeIcon icon={faCalendarDays} className="text-stone-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">{t('projects.year')}</h3>
                <p className="text-lg font-semibold text-gray-900">{project.year}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-100">
                <FontAwesomeIcon icon={faMoneyBill} className="text-stone-600" />
              </div>
              {project.cost > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">{t('projects.cost')}</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {Number(project.cost).toLocaleString()} {project.currency}
                </p>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        {gallery_images.length > 0 && (
          <div className="mb-8">
            <h2 className="mb-4 text-2xl font-semibold">{t('projects.gallery')}</h2>
            <Swiper
              modules={[Navigation, Pagination, Autoplay, Zoom, Mousewheel]}
              dir={isRTL ? 'rtl' : 'ltr'}
              spaceBetween={10}
              slidesPerView={1.2}
              navigation={false}
              zoom={true}
              pagination={{
                clickable: true,
                el: '.swiper-pagination'
              }}
              className="gallery-swiper"
            >
              {gallery_images.map((image, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="aspect-[4/3] cursor-pointer rounded-lg overflow-hidden"
                    onClick={() => {
                      setCurrentImage(index);
                      setShowGalleryModal(true);
                    }}
                  >
                    <LoadingImage
                      src={image}
                      alt=""
                      className="object-cover w-full h-full"
                    />
                  </div>
                </SwiperSlide>
              ))}
              <div className="swiper-pagination !bottom-2"></div>
            </Swiper>
          </div>
        )}

        {/* Contact Button */}
        <div className="py-4 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center px-6 py-3 text-base font-semibold text-white rounded-lg shadow-lg transition-colors bg-stone-800 hover:bg-stone-700"
          >
            {t('footer.contact')}
            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </Link>
        </div>
      </div>

      {/* Modal */}
      {showGalleryModal && (
        <ImageModal
          images={gallery_images}
          onClose={() => setShowGalleryModal(false)}
          initialSlide={currentImage}
          isRTL={isRTL}
        />
      )}
    </div>
  );
};

export default ProjectDetailMobile;
